import React, { FC, memo } from 'react';
import DOMPurify from 'dompurify';

/**
 * SafeHtmlRenderer component props
 */
interface SafeHtmlRendererProps {
  /**
   * The HTML content to be sanitized and rendered
   */
  html: string;
  
  /**
   * Additional CSS class names to apply to the wrapper element
   */
  className?: string;
  
  /**
   * Custom DOMPurify configuration options
   * @see https://github.com/cure53/DOMPurify#can-i-configure-dompurify
   */
  sanitizeOptions?: DOMPurify.Config;
}

/**
 * SafeHtmlRenderer component
 * 
 * A component that safely renders HTML content using DOMPurify to sanitize
 * the input and protect against XSS attacks.
 * 
 * @example
 * ```tsx
 * <SafeHtmlRenderer html="<p>This is <strong>safe</strong> HTML</p>" />
 * ```
 * 
 * @example
 * ```tsx
 * // With custom sanitize options
 * <SafeHtmlRenderer 
 *   html={userGeneratedContent} 
 *   sanitizeOptions={{ ALLOWED_TAGS: ['p', 'strong', 'em', 'a'] }}
 * />
 * ```
 */
const SafeHtmlRenderer: FC<SafeHtmlRendererProps> = ({ 
  html, 
  className = '', 
  sanitizeOptions = {} 
}) => {
  // Sanitize the HTML content using DOMPurify
  const sanitizedHtml = DOMPurify.sanitize(html, sanitizeOptions);
  
  return (
    <div 
      className={className}
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
    />
  );
};

export default memo(SafeHtmlRenderer);